import { Fragment, useState, type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PlanStage } from '../api/plans';
import type { RouteLegDto } from '@camper-diary/shared';
import { StageLodgingView } from './StagesSection';

type Props = {
  stages: PlanStage[];
  /** legs[i] = segmento fra stages[i] e stages[i+1] */
  legs?: RouteLegDto[] | null;
  /** true se i legs sono in fase di refetch (UI mostra "…") */
  legsLoading?: boolean;
  busy?: boolean;
  /** id della tappa attualmente in editing (se presente, sotto la card si apre l'editor inline). */
  editingStageId?: string | null;
  /** Editor da renderizzare inline sotto la tappa in editing. */
  inlineEditor?: ReactNode;
  onReorder: (newOrder: PlanStage[]) => void;
  onEdit: (stage: PlanStage) => void;
  onDelete: (stage: PlanStage) => void;
  onDateChange: (stage: PlanStage, newDate: string | null) => void;
};

export function SortablePlanStages({
  stages,
  legs,
  legsLoading,
  busy,
  editingStageId,
  inlineEditor,
  onReorder,
  onEdit,
  onDelete,
  onDateChange,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = stages.findIndex((s) => s.id === active.id);
    const newIdx = stages.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(arrayMove(stages, oldIdx, newIdx));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={stages.map((s) => s.id)}
        strategy={verticalListSortingStrategy}
      >
        <ol className="plan-stage-list">
          {stages.map((s, i) => {
            const leg = i < stages.length - 1 ? legs?.[i] : undefined;
            return (
              <Fragment key={s.id}>
                <SortableStage
                  stage={s}
                  index={i + 1}
                  busy={busy}
                  isEditing={editingStageId === s.id}
                  onEdit={() => onEdit(s)}
                  onDelete={() => onDelete(s)}
                  onDateChange={(d) => onDateChange(s, d)}
                />
                {editingStageId === s.id && inlineEditor && (
                  <li className="plan-stage-edit-row" aria-live="polite">
                    {inlineEditor}
                  </li>
                )}
                {i < stages.length - 1 && (
                  <li className="plan-leg" aria-hidden="true">
                    {legsLoading ? (
                      <span className="muted small">↓ calcolo distanza…</span>
                    ) : leg ? (
                      <span className="leg-text">
                        ↓ <strong>{Math.round(leg.distanceKm)} km</strong> ·{' '}
                        {formatDuration(leg.durationMinutes)}
                      </span>
                    ) : (
                      <span className="muted small">↓ —</span>
                    )}
                  </li>
                )}
              </Fragment>
            );
          })}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function SortableStage({
  stage,
  index,
  busy,
  isEditing,
  onEdit,
  onDelete,
  onDateChange,
}: {
  stage: PlanStage;
  index: number;
  busy?: boolean;
  isEditing?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onDateChange: (d: string | null) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [date, setDate] = useState(stage.plannedDate ?? '');

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`plan-stage-item${isEditing ? ' editing' : ''}`}
    >
      <button
        type="button"
        className="drag-handle"
        {...attributes}
        {...listeners}
        aria-label="Trascina per riordinare"
        title="Trascina per riordinare"
      >
        ⋮⋮
      </button>
      <span className="num">{index}</span>
      <div className="stage-body">
        <h3 className="cd-title-upper">{stage.title}</h3>
        {stage.address && <p className="muted small">{stage.address}</p>}
        <p className="coords">
          {stage.lat.toFixed(5)}, {stage.lon.toFixed(5)}
        </p>
        {stage.info && <p className="info small">{stage.info}</p>}
        <StageLodgingView lodging={stage.lodging} busy={busy} />
      </div>
      <label className="stage-date">
        <span className="muted small">Data</span>
        <input
          type="date"
          value={date}
          onChange={(e) => {
            const v = e.target.value || '';
            setDate(v);
            onDateChange(v || null);
          }}
          disabled={busy}
        />
      </label>
      <div className="stage-actions">
        <button className="btn btn-ghost" disabled={busy} onClick={onEdit}>
          {isEditing ? 'In modifica…' : 'Modifica'}
        </button>
        <button className="btn btn-danger" disabled={busy} onClick={onDelete}>
          Elimina
        </button>
      </div>
    </li>
  );
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
